import type { ApiResponse } from '@/types';

export const BASE_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:9001';

function getToken(): string | null {
    return localStorage.getItem('md_token');
}

function buildHeaders(isMultipart = false): HeadersInit {
    const token = getToken();
    const headers: Record<string, string> = {};

    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }

    if (!isMultipart) {
        headers['Content-Type'] = 'application/json';
    }

    return headers;
}

async function handleResponse<T>(res: Response): Promise<ApiResponse<T>> {
    const json = await res.json();
    if (!res.ok) {
        const message = json?.response?.message ?? 'Something went wrong';
        throw new Error(message);
    }
    return json as ApiResponse<T>;
}

export const api = {
    async get<T>(path: string): Promise<ApiResponse<T>> {
        const res = await fetch(`${BASE_URL}${path}`, {
            method: 'GET',
            headers: buildHeaders(),
        });
        return handleResponse<T>(res);
    },

    async post<T>(path: string, body?: unknown): Promise<ApiResponse<T>> {
        const res = await fetch(`${BASE_URL}${path}`, {
            method: 'POST',
            headers: buildHeaders(),
            body: body !== undefined ? JSON.stringify(body) : undefined,
        });
        return handleResponse<T>(res);
    },

    async patch<T>(path: string, body?: unknown): Promise<ApiResponse<T>> {
        const res = await fetch(`${BASE_URL}${path}`, {
            method: 'PATCH',
            headers: buildHeaders(),
            body: body !== undefined ? JSON.stringify(body) : undefined,
        });
        return handleResponse<T>(res);
    },

    async put<T>(path: string, body?: unknown): Promise<ApiResponse<T>> {
        const res = await fetch(`${BASE_URL}${path}`, {
            method: 'PUT',
            headers: buildHeaders(),
            body: body !== undefined ? JSON.stringify(body) : undefined,
        });
        return handleResponse<T>(res);
    },

    async delete<T>(path: string): Promise<ApiResponse<T>> {
        const res = await fetch(`${BASE_URL}${path}`, {
            method: 'DELETE',
            headers: buildHeaders(),
        });
        return handleResponse<T>(res);
    },

    async upload<T>(path: string, formData: FormData): Promise<ApiResponse<T>> {
        const res = await fetch(`${BASE_URL}${path}`, {
            method: 'POST',
            headers: buildHeaders(true),
            body: formData,
        });
        return handleResponse<T>(res);
    },
};
